import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, GraduationCap, Users, Search, AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { SchoolClass, ClassFormData } from '../../types';
import { classesApi } from '../../services/api';
import { ConfirmDialog } from '../ui';
import { sortClasses } from '../../utils/classUtils';

const classSchema = yup.object({
  name: yup
    .string()
    .required('Class name is required')
    .min(1, 'Class name must be at least 1 character')
    .max(50, 'Class name must not exceed 50 characters'),
  sections: yup
    .array()
    .of(yup.string().required())
    .min(1, 'At least one section is required')
    .required(),
  isActive: yup
    .boolean()
    .required()
}).required();

const ClassManagement: React.FC = () => {
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState<SchoolClass | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newSection, setNewSection] = useState('');

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = useForm<ClassFormData>({
    resolver: yupResolver(classSchema) as any,
    defaultValues: {
      name: '',
      sections: ['A'],
      isActive: true
    }
  });

  const sections = watch('sections');

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      setLoading(true);
      const classesData = await classesApi.getAll();
      const sortedClasses = sortClasses(classesData);
      setClasses(sortedClasses);
    } catch (error) {
      toast.error('Failed to load classes');
      console.error('Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClass = async (data: any) => {
    try {
      setSubmitting(true);
      const formData: ClassFormData = data as ClassFormData;
      const newClass = await classesApi.create(formData);
      setClasses(prev => [newClass, ...prev]);
      setShowForm(false);
      reset();
      toast.success('Class created successfully');
    } catch (error) {
      toast.error('Failed to create class');
      console.error('Error creating class:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClass = async (data: any) => {
    if (!editingClass) return;

    try {
      setSubmitting(true);
      // When editing, only update sections and isActive, keep original name
      const formData: ClassFormData = {
        name: editingClass.name, // Keep original name
        sections: data.sections,
        isActive: data.isActive
      };
      await classesApi.update(editingClass.id, formData);
      setClasses(prev =>
        prev.map(cls =>
          cls.id === editingClass.id
            ? {
                ...cls,
                ...formData,
                updatedAt: new Date().toISOString()
              }
            : cls
        )
      );
      setEditingClass(null);
      setShowForm(false);
      reset();
      toast.success('Class updated successfully');
    } catch (error) {
      toast.error('Failed to update class');
      console.error('Error updating class:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClass = (classItem: SchoolClass) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Class',
      message: `Are you sure you want to delete "${classItem.name}"?\n\nThis action cannot be undone and will remove the class and all its sections permanently.`,
      onConfirm: async () => {
        try {
          await classesApi.delete(classItem.id);
          setClasses(prev => prev.filter(c => c.id !== classItem.id));
          toast.success('Class deleted successfully');
        } catch (error) {
          toast.error('Failed to delete class');
          console.error('Error deleting class:', error);
        }
      }
    });
  };

  const handleEdit = (classItem: SchoolClass) => {
    setEditingClass(classItem);
    setValue('name', classItem.name);
    setValue('sections', classItem.sections);
    setValue('isActive', classItem.isActive);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingClass(null);
    reset();
  };

  const addSection = () => {
    if (newSection.trim() && !sections.includes(newSection.trim())) {
      setValue('sections', [...sections, newSection.trim()]);
      setNewSection('');
    }
  };

  const removeSection = (index: number) => {
    const updatedSections = sections.filter((_, i) => i !== index);
    setValue('sections', updatedSections);
  };

  const filteredClasses = sortClasses(
    classes.filter(cls =>
      cls.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  if (showForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-[#f3ede2]">
              {editingClass ? 'Edit Class' : 'Create New Class'}
            </h3>
            <p className="text-[#b9ad9b] text-sm mt-1">
              {editingClass ? 'Update sections and status (class name cannot be changed)' : 'Add a new class with sections'}
            </p>
          </div>
        </div>

        <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
          <form onSubmit={handleSubmit(editingClass ? handleEditClass : handleCreateClass)} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Class Name *
              </label>
              <input
                type="text"
                {...register('name')}
                placeholder="Enter class name (e.g., Class 1, Class 10, Nursery)"
                disabled={!!editingClass}
                className={`w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent ${
                  editingClass ? 'bg-[#28221a] text-[#8a7f6e] cursor-not-allowed' : ''
                }`}
              />
              {editingClass && (
                <p className="text-[#8a7f6e] text-sm mt-1">
                  <AlertCircle className="w-4 h-4 inline-block mr-1" />
                  Class name cannot be changed after creation
                </p>
              )}
              {errors.name && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Sections *
              </label>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newSection}
                    onChange={(e) => setNewSection(e.target.value)}
                    placeholder="Enter section name (e.g., A, B, C)"
                    className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] flex-1 px-3 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSection())}
                  />
                  <button
                    type="button"
                    onClick={addSection}
                    className="px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {sections.map((section, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-2 px-3 py-1 bg-[#16212b] text-[#bfdbfe] rounded-full text-sm"
                    >
                      <span>{section}</span>
                      {sections.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSection(index)}
                          className="text-[#60a5fa] hover:text-[#bfdbfe]"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {errors.sections && (
                <p className="text-[#f38b83] text-sm mt-1">{errors.sections.message}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('isActive')}
                id="isActive"
                className="w-4 h-4 text-[#e2916d] border-[#3a3122] rounded focus:ring-[#e2916d]"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-[#b9ad9b]">
                Active
              </label>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-[#3a3122]">
              <button
                type="button"
                onClick={handleCancelForm}
                className="px-4 py-2 text-[#b9ad9b] border border-[#3a3122] rounded-lg hover:bg-[#28221a] transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {submitting ? 'Saving...' : editingClass ? 'Update' : 'Create'} Class
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2]">Class Management</h3>
          <p className="text-[#b9ad9b] text-sm mt-1">Manage school classes and their sections</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Class</span>
        </button>
      </div>

      <div className="bg-[#211c15] rounded-xl border border-[#3a3122]">
        <div className="p-6 border-b border-[#3a3122]">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-[#8a7f6e]" />
              <input
                type="text"
                placeholder="Search classes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] pl-10 pr-4 py-2 border border-[#3a3122] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e2916d] focus:border-transparent w-full"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-[#e2916d] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredClasses.length === 0 ? (
          <div className="text-center py-12">
            <GraduationCap className="w-12 h-12 text-[#8a7f6e] mx-auto mb-4" />
            <p className="text-[#b9ad9b] text-lg">
              {classes.length === 0 ? 'No classes found' : 'No classes match your search'}
            </p>
            <p className="text-[#8a7f6e] text-sm">
              {classes.length === 0 ? 'Create your first class to get started' : 'Try adjusting your search criteria'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[#3a3122]">
            {filteredClasses.map((classItem) => (
              <div key={classItem.id} className="p-6 hover:bg-[#28221a] transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-[#16212b] rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-[#60a5fa]" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="font-medium text-[#f3ede2]">{classItem.name}</h4>
                        {!classItem.isActive && (
                          <span className="px-2 py-1 bg-[#b9ad9b] text-[#5c5346] rounded-full text-xs">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <Users className="w-4 h-4 text-[#8a7f6e]" />
                        <span className="text-[#b9ad9b] text-sm">
                          {classItem.sections.length} section{classItem.sections.length !== 1 ? 's' : ''}:
                        </span>
                        <div className="flex flex-wrap gap-1">
                          {classItem.sections.map((section, index) => (
                            <span
                              key={index}
                              className="px-2 py-0.5 bg-[#16212b] text-[#bfdbfe] rounded text-xs"
                            >
                              {section}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(classItem)}
                      className="p-2 text-[#8a7f6e] hover:text-[#f0b64c] hover:bg-[#332711] rounded-lg transition-colors"
                      title="Edit Class"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClass(classItem)}
                      className="p-2 text-[#8a7f6e] hover:text-[#f38b83] hover:bg-[#341a17] rounded-lg transition-colors"
                      title="Delete Class"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="danger"
      />
    </div>
  );
};

export default ClassManagement;